import React from 'react';
import { Card, Switch, Tooltip, Collapse, Divider, Row, Col, Typography, Space, Tag } from 'antd';
import {
  QuestionCircleOutlined,
  SettingOutlined,
  DashboardOutlined,
  SearchOutlined,
  GlobalOutlined,
  BellOutlined,
  UsergroupAddOutlined,
  ProjectOutlined,
  TeamOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import {
  orgFeaturesInfo,
  orgConfigInfo,
  orgConfigNaming,
} from '../../../../core/utils/constants/constants';

const { Panel } = Collapse;
const { Text } = Typography;

// Add custom styles to override Ant Design's internal styling
const customStyles = `
  .aligned-collapse .ant-collapse-content-box {
    padding-left: 0 !important;
    padding-right: 0 !important;
    background-color: #f8fafc !important;
  }
  
  .aligned-collapse .ant-collapse-header {
    padding: 15px 24px !important;
    padding-right: 108px !important;
    position: relative !important;
    background: #ffffff !important;
    transition: all 0.3s ease !important;
    border-radius: 12px !important;
    min-height: 64px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
  }
  
  /* Icon and text wrapper for better alignment */
  .aligned-collapse .ant-collapse-header-text {
    display: flex !important;
    align-items: center !important;
    flex: 1 !important;
  }
  
  /* Ensure both icon containers are exactly aligned */
  .aligned-collapse .ant-collapse-expand-icon,
  .aligned-collapse .ant-collapse-header [style*="34px"] {
    margin: 0 !important;
    vertical-align: middle !important;
  }
  
  /* Force icon containers to align perfectly */
  .aligned-collapse .ant-collapse-header > div:first-child {
    display: flex !important;
    align-items: center !important;
  }
  
  .aligned-collapse .ant-collapse-header:hover {
    background: #f8fafc !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
  }
  
  .aligned-collapse .ant-collapse-item {
    border: none !important;
    margin-bottom: 8px !important;
    background: #ffffff !important;
    border-radius: 12px !important;
    overflow: hidden !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
    transition: all 0.3s ease !important;
  }
  
  .aligned-collapse .ant-collapse-item:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  }
  
  .aligned-collapse .ant-collapse-item-active {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  }
  
  .aligned-collapse .ant-collapse-expand-icon {
    position: absolute !important;
    right: 64px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    background: #f1f5f9 !important;
    border-radius: 8px !important;
    width: 34px !important;
    height: 34px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: all 0.3s ease !important;
    flex-shrink: 0 !important;
  }
  
  .aligned-collapse .ant-collapse-item:hover .ant-collapse-expand-icon {
    background: #e2e8f0 !important;
  }
  
  .aligned-collapse .ant-collapse-item-active .ant-collapse-expand-icon {
    background: #e2e8f0 !important;
  }
  
  .aligned-collapse .ant-collapse-header .anticon-right {
    font-size: 11px !important;
    transition: transform 0.3s ease !important;
  }
  
  .aligned-collapse .ant-collapse-item-active .anticon-right {
    transform: rotate(90deg) !important;
  }
  
  /* Ensure the extra content (toggle) is positioned correctly */
  .aligned-collapse .ant-collapse-extra {
    position: absolute !important;
    right: 24px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    display: flex !important;
    align-items: center !important;
  }
  
  /* Better transitions */
  .aligned-collapse .ant-motion-collapse {
    transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
  
  /* Custom switch styling */
  .aligned-collapse .ant-switch {
    background-color: #cbd5e1 !important;
    transition: all 0.3s ease !important;
  }
  
  .aligned-collapse .ant-switch-checked {
    background-color: #3b82f6 !important;
  }
  
  .aligned-collapse .ant-switch:focus {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
  }
  
  .aligned-collapse .ant-switch-disabled {
    opacity: 0.4 !important;
    cursor: not-allowed !important;
  }
  
  .aligned-collapse .ant-switch-disabled.ant-switch-checked {
    background-color: #93bbfe !important;
  }
`;

const GroupedPermissionsToggler = (props) => {
  const {
    loggedInPermissions,
    currentPermissions,
    config,
    disabled,
    onSwitchChange,
    isAdmin,
    isMJParent,
    isSelfService,
    isProcessor,
    orgTab,
    permissionsConfig,
  } = props;

  // Add style tag to document head if it doesn't exist
  React.useEffect(() => {
    const styleId = 'grouped-permissions-toggler-styles';
    if (!document.getElementById(styleId)) {
      const styleTag = document.createElement('style');
      styleTag.id = styleId;
      styleTag.innerHTML = customStyles;
      document.head.appendChild(styleTag);
    }

    return () => {
      // Cleanup on unmount
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // Feature-Configuration mapping
  const featureConfigMapping = {
    ORGS: ['canResetPasswords', 'isAdmin'],
    CAMPAIGNS: [
      'providersVisible',
      'isTrafficker',
      'exportData',
      'isSelfService',
      'hasInsights',
      'priceVisible',
      'isProcessor',
      'campaignSummary',
      'isPacing',
      'pastData',
      'segment',
      'amplify',
      'sitesAppsTransactions',
      'isPartner',
    ],
    PAID_SEARCH: ['configurator', 'exportData'],
    SEO: ['configurator', 'exportData'],
    EDITORIAL: ['exportData', 'isTrafficker', 'isSelfService', 'billing'],
    TASK_MANAGEMENT: ['exportData'],
  };

  // Display name mapping for features
  const getFeatureDisplayName = (feature) => {
    switch (feature) {
      case 'CAMPAIGNS':
        return 'DISPLAY';
      case 'DEMOGRAPHIC':
        return 'DEMOGRAPH';
      case 'EDITORIAL':
        return 'ATTENTION SIGNALS';
      default:
        return feature.replace(/_/g, ' ');
    }
  };

  // Get available features based on logged in permissions
  const availableFeatures = loggedInPermissions
    ? Object.keys(loggedInPermissions).filter((feature) => feature !== 'ENABLED')
    : [];

  // Determine which configurations are allowed (adapted from original ConfigurationToggler)
  const getAllowedConfigs = () => {
    let allowedKeys = [];

    if (permissionsConfig && !disabled) {
      Object.values(permissionsConfig).forEach((val, i) => {
        if (orgTab) {
          if (
            val ||
            Object.keys(permissionsConfig)[i] === 'isSelfService' ||
            (isMJParent ? Object.keys(permissionsConfig)[i] === 'isPartner' : null)
          ) {
            allowedKeys.push(Object.keys(permissionsConfig)[i]);
          }

          if (
            permissionsConfig.isProcessor &&
            Object.keys(permissionsConfig)[i] === 'isSelfService'
          ) {
            allowedKeys.push(Object.keys(permissionsConfig)[i]);
          }
        } else {
          if (val || (isMJParent ? Object.keys(permissionsConfig)[i] === 'isPartner' : null)) {
            allowedKeys.push(Object.keys(permissionsConfig)[i]);
          }
        }
      });
    } else {
      if (permissionsConfig) {
        Object.values(permissionsConfig).forEach((val, i) => {
          if (val || isAdmin) {
            allowedKeys.push(Object.keys(permissionsConfig)[i]);
          }
        });
      }
    }

    return [...new Set(allowedKeys)];
  };

  const allowedConfigs = getAllowedConfigs();

  // Styles for modern, professional look
  const styles = {
    container: {
      margin: '0',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '20px',
      padding: '32px',
      boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.03)',
      border: '1px solid rgba(226, 232, 240, 0.8)',
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#0f172a',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      letterSpacing: '-0.025em',
    },
    featureCard: {
      marginBottom: '12px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease-in-out',
      background: '#ffffff',
      overflow: 'hidden',
    },
    featureCardHover: {
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      borderColor: '#3b82f6',
    },
    collapsePanel: {
      borderRadius: '12px',
      marginBottom: '8px',
      border: 'none',
      background: '#ffffff',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
      overflow: 'hidden',
    },
    panelHeader: {
      background: '#ffffff',
      borderBottom: '1px solid #e2e8f0',
      padding: '15px 24px' /* Match CSS padding */,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      minHeight: '64px',
    },
    featureTitle: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#0f172a',
      letterSpacing: '-0.015em',
    },
    configRow: {
      padding: '15px 24px' /* Match header padding for alignment */,
      borderBottom: '1px solid #f1f5f9',
      transition: 'all 0.2s ease',
      background: '#ffffff',
      cursor: 'pointer',
    },
    configRowHover: {
      background: '#f8fafc',
    },
    configLabel: {
      fontSize: '14px',
      color: '#475569',
      fontWeight: '500',
      letterSpacing: '-0.01em',
    },
    betaCard: {
      marginTop: '20px',
      borderRadius: '12px',
      border: '2px solid #fbbf24',
      background: '#fffbeb',
      overflow: 'hidden',
      boxShadow: '0 1px 3px 0 rgba(251, 191, 36, 0.1)',
    },
    betaHeader: {
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: '#ffffff',
      fontWeight: '600',
      padding: '16px 24px',
    },
    emptyState: {
      padding: '32px 24px',
      textAlign: 'center',
      color: '#94a3b8',
      fontStyle: 'normal',
      background: '#f8fafc',
      borderRadius: '0',
      border: 'none',
      fontSize: '13px',
      fontWeight: '500',
      letterSpacing: '-0.01em',
    },
    switch: {
      transform: 'scale(1.05)',
      transition: 'all 0.2s ease',
    },
  };

  // Icon mapping for features
  const getFeatureIcon = (feature) => {
    const iconStyle = {
      fontSize: '16px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '34px',
      height: '34px',
      flexShrink: 0,
    };

    const iconMap = {
      ORGS: (
        <div style={{ ...iconStyle, background: '#dbeafe', color: '#2563eb' }}>
          <TeamOutlined />
        </div>
      ),
      CAMPAIGNS: (
        <div style={{ ...iconStyle, background: '#d1fae5', color: '#059669' }}>
          <DashboardOutlined />
        </div>
      ),
      PAID_SEARCH: (
        <div style={{ ...iconStyle, background: '#e9d5ff', color: '#7c3aed' }}>
          <SearchOutlined />
        </div>
      ),
      SEO: (
        <div style={{ ...iconStyle, background: '#fed7aa', color: '#ea580c' }}>
          <GlobalOutlined />
        </div>
      ),
      EDITORIAL: (
        <div style={{ ...iconStyle, background: '#fee2e2', color: '#dc2626' }}>
          <BellOutlined />
        </div>
      ),
      AUDIENCES: (
        <div style={{ ...iconStyle, background: '#e0e7ff', color: '#4f46e5' }}>
          <UsergroupAddOutlined />
        </div>
      ),
      SEGMENTS: (
        <div style={{ ...iconStyle, background: '#fce7f3', color: '#db2777' }}>
          <ProjectOutlined />
        </div>
      ),
      DEMOGRAPHIC: (
        <div style={{ ...iconStyle, background: '#ccfbf1', color: '#0d9488' }}>
          <UsergroupAddOutlined />
        </div>
      ),
      TASK_MANAGEMENT: (
        <div style={{ ...iconStyle, background: '#ffedd5', color: '#ea580c' }}>
          <ProjectOutlined />
        </div>
      ),
      HORIZONTAL_NAVIGATION: (
        <div style={{ ...iconStyle, background: '#e2e8f0', color: '#475569' }}>
          <MenuOutlined />
        </div>
      ),
    };
    return (
      iconMap[feature] || (
        <div style={{ ...iconStyle, background: '#e2e8f0', color: '#475569' }}>
          <SettingOutlined />
        </div>
      )
    );
  };

  // Render configuration toggle
  const renderConfigToggle = (configKey, isLast = false) => {
    // Skip if config doesn't exist or is filtered out
    if (configKey === 'articles' || !orgConfigNaming[configKey]) {
      return null;
    }

    // Check if this config is allowed
    if (!allowedConfigs.includes(configKey)) {
      return null;
    }

    let selfService = false;
    if (isProcessor) {
      selfService = false;
    } else if (isSelfService) {
      selfService = true;
    } else {
      selfService = true;
    }

    return (
      <div
        key={configKey}
        style={{
          ...styles.configRow,
          borderBottom: isLast ? 'none' : styles.configRow.borderBottom,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = styles.configRowHover.background;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = styles.configRow.background;
        }}
      >
        <Row align="middle" justify="space-between" style={{ minHeight: '34px' }}>
          <Col flex="auto">
            <Text style={styles.configLabel}>{orgConfigNaming[configKey]}</Text>
          </Col>
          <Col>
            <Tooltip title={orgConfigInfo[configKey]} placement="left">
              <Switch
                checked={config[configKey] ? config[configKey] : false}
                name={`${configKey}-CONFIG`}
                disabled={
                  orgConfigNaming[configKey] === 'Self Service' && !isMJParent && !orgTab
                    ? selfService
                    : disabled
                }
                onChange={(checked) => {
                  onSwitchChange(checked, `${configKey}-CONFIG`);
                }}
                style={styles.switch}
                size="default"
              />
            </Tooltip>
          </Col>
        </Row>
      </div>
    );
  };

  // Render grouped feature with configurations
  const renderGroupedFeature = (feature) => {
    const configs = featureConfigMapping[feature] || [];
    const featureEnabled = currentPermissions[feature]?.includes('WRITE');
    const validConfigs = configs.filter(
      (configKey) => configKey !== 'articles' && orgConfigNaming[configKey]
    );
    const hasConfigurations = validConfigs.length > 0;

    return (
      <Panel
        key={feature}
        header={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              paddingLeft: '4px',
              height: '100%',
            }}
          >
            {getFeatureIcon(feature)}
            <Text style={styles.featureTitle}>{getFeatureDisplayName(feature)}</Text>
          </div>
        }
        extra={
          <Tooltip title={orgFeaturesInfo[feature]} placement="left">
            <Switch
              checked={featureEnabled}
              name={`${feature}-WRITE`}
              disabled={disabled || !loggedInPermissions[feature]?.includes('WRITE')}
              onChange={(checked) => {
                onSwitchChange(checked, `${feature}-WRITE`);
              }}
              onClick={(checked, e) => e.stopPropagation()} // Important to prevent toggle collapsing
              style={styles.switch}
              size="default"
            />
          </Tooltip>
        }
        style={styles.collapsePanel}
        showArrow={hasConfigurations}
        collapsible={hasConfigurations ? 'header' : 'disabled'}
      >
        {hasConfigurations ? (
          featureEnabled ? (
            <div style={{ background: '#ffffff' }}>
              {(() => {
                const validConfigElements = configs
                  .map((configKey, index) => {
                    if (
                      configKey !== 'articles' &&
                      orgConfigNaming[configKey] &&
                      allowedConfigs.includes(configKey)
                    ) {
                      return renderConfigToggle(configKey, false);
                    }
                    return null;
                  })
                  .filter(Boolean);

                // Update the last valid config to not have a border
                if (validConfigElements.length > 0) {
                  const lastConfig = configs
                    .filter(
                      (key) =>
                        key !== 'articles' && orgConfigNaming[key] && allowedConfigs.includes(key)
                    )
                    .pop();

                  return configs
                    .map((configKey) => {
                      const isLast = configKey === lastConfig;
                      return renderConfigToggle(configKey, isLast);
                    })
                    .filter(Boolean);
                }

                return validConfigElements;
              })()}
            </div>
          ) : (
            <div style={styles.emptyState}>
              <Text type="secondary">Enable this feature to access configurations</Text>
            </div>
          )
        ) : null}
      </Panel>
    );
  };

  // Render Beta configuration section
  const renderBetaSection = () => {
    if (!allowedConfigs.includes('isBeta')) {
      return null;
    }

    return (
      <Card
        title={
          <Space align="center">
            <div
              style={{
                background: '#e0f2fe',
                color: '#1d4ed8',
                padding: '6px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SettingOutlined style={{ fontSize: '16px' }} />
            </div>
            <Text
              style={{
                color: '#1d4ed8',
                fontWeight: '500',
                fontSize: '15px',
                letterSpacing: '0.5px',
              }}
            >
              Platform Preview
            </Text>
            <Tag
              color="#dbeafe"
              style={{
                color: '#1d4ed8',
                marginLeft: '8px',
                fontWeight: 500,
                fontSize: '11px',
                borderRadius: '4px',
                padding: '2px 8px',
              }}
            >
              BETA
            </Tag>
          </Space>
        }
        style={{
          marginTop: '20px',
          borderRadius: '12px',
          border: '1px solid #dbeafe',
          background: '#f8fafc',
          overflow: 'hidden',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        }}
        headStyle={{
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          color: '#1d4ed8',
          fontWeight: '500',
          padding: '12px 24px',
          borderBottom: '1px solid #dbeafe',
        }}
        bodyStyle={{ padding: 0 }}
      >
        {renderConfigToggle('isBeta', true)}
      </Card>
    );
  };

  if (availableFeatures.length === 0) {
    return null;
  }

  return (
    <div style={styles.container}>
      {/* All Features & Configurations */}
      <div style={styles.sectionTitle}>
        <div
          style={{
            background: '#e2e8f0',
            color: '#475569',
            padding: '8px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SettingOutlined style={{ fontSize: '18px' }} />
        </div>
        <Text
          style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#0f172a',
            letterSpacing: '-0.025em',
          }}
        >
          Features & Configurations
        </Text>
      </div>
      <Collapse
        expandIconPosition="right"
        className="aligned-collapse"
        style={{
          background: 'transparent',
          border: 'none',
        }}
      >
        {availableFeatures.map((feature) => renderGroupedFeature(feature))}
      </Collapse>

      {/* Beta Platform Settings */}
      {renderBetaSection()}
    </div>
  );
};

export default GroupedPermissionsToggler;
